import {
  AbstractControl,
  AbstractControlOptions,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  CalendarResponseData,
  DragDropResponseData,
  FileUploadResponseData,
  HierarchicalResponseData,
  OpenAnswerResponseData,
  PriorityDropdownResponseData,
  QuestionAnswerResponseData,
  SingleSelectResponseData,
} from '@shared/models/question-answer-response';
import { Calendar } from '@shared/models/question-types/calendar';
import { FileUpload } from '@shared/models/question-types/file-upload';
import { Hierarchical } from '@shared/models/question-types/hierarchical';
import { Matrix } from '@shared/models/question-types/matrix';
import { MultiSelect } from '@shared/models/question-types/multi-select';
import { OpenAnswer } from '@shared/models/question-types/open-answer';
import {
  PriorityDragDrop,
  PriorityDragDropItem,
} from '@shared/models/question-types/priority-dragdrop';
import { PriorityDropdown } from '@shared/models/question-types/priority-dropdown';
import { SingleSelect } from '@shared/models/question-types/single-select';
import { Question } from '@shared/models/views-models/question-view';
import { MultiMatrix } from '../models/question-types/multi-matrix';
import { getValidators, maxLengthValidator } from './validators.util';

export const createForm = (
  formBuilder: FormBuilder,
  questions: Question[],
  answers?: QuestionAnswerResponseData[],
  enableValidators = true,
): FormGroup => {
  const group: { [key: string]: AbstractControl<any, any> } = {};

  questions.forEach((question) => {
    const existingAnswer = answers?.find(
      (ans) => ans.questionId === question.id,
    );
    switch (question.type) {
      case 'OPENANSWER':
        handleOpenAnswerQuestion(
          group,
          formBuilder,
          question as OpenAnswer,
          existingAnswer as OpenAnswerResponseData,
          enableValidators,
        );
        break;

      case 'MATRIX':
        handleMatrixQuestion(
          group,
          formBuilder,
          question as Matrix,
          answers,
          enableValidators,
        );
        break;

      case 'MULTI_MATRIX':
        handleMultiMatrixQuestion(
          group,
          formBuilder,
          question as MultiMatrix,
          answers,
          enableValidators,
        );
        break;

      case 'SINGLE_CHOICE':
        handleSingleChoiceQuestion(
          group,
          formBuilder,
          question as SingleSelect,
          existingAnswer as SingleSelectResponseData,
          enableValidators,
        );
        break;

      case 'MULTIPLE_CHOICE':
        handleMultipleChoiceQuestion(
          group,
          formBuilder,
          question as MultiSelect,
          answers,
          enableValidators,
        );
        break;

      case 'HIERARCHICAL':
        handleHierarchicalQuestion(
          group,
          formBuilder,
          question as Hierarchical,
          answers,
          enableValidators,
        );
        break;

      case 'INFORMATION':
        break;

      case 'PRIORITY_DROPDOWN':
        handlePriorityDropdownQuestion(
          group,
          formBuilder,
          question as PriorityDropdown,
          answers,
          enableValidators,
        );
        break;

      case 'CALENDAR':
        handleCalendarQuestion(
          group,
          formBuilder,
          question as Calendar,
          answers,
          enableValidators,
        );
        break;

      case 'FILE_UPLOAD':
        handleFileUploadQuestion(
          group,
          formBuilder,
          question as FileUpload,
          answers,
          enableValidators,
        );
        break;

      case 'PRIORITY_DRAGDROP': {
        handlePriorityDragDropQuestion(
          group,
          formBuilder,
          question,
          answers?.find(
            (i) => i.questionId === question.id,
          ) as unknown as DragDropResponseData,
          enableValidators,
        );
        break;
      }
      default:
        console.error('Unknown question type');
    }
  });

  return new FormGroup(group);
};

function handleOpenAnswerQuestion(
  group: { [key: string]: AbstractControl<any, any> },
  formBuilder: FormBuilder,
  question: OpenAnswer,
  existingAnswer?: OpenAnswerResponseData,
  enableValidators?: boolean,
) {
  const defaultValue =
    existingAnswer && existingAnswer.$type === 'openAnswer'
      ? existingAnswer.answer
      : question.defaultAnswer.enabled
        ? question.defaultAnswer.defaultAnswer
        : '';

  group[question.id] = formBuilder.control(
    defaultValue,
    getValidators(question, enableValidators),
  );
}

function handleMultiMatrixQuestion(
  group: { [key: string]: AbstractControl<any, any> },
  formBuilder: FormBuilder,
  question: MultiMatrix,
  answers?: QuestionAnswerResponseData[],
  enableValidators?: boolean,
) {
  question.columns.forEach((column) => {
    handleMatrixQuestion(group, formBuilder, column, answers, enableValidators);
  });
}

function handleMatrixQuestion(
  group: { [key: string]: AbstractControl<any, any> },
  formBuilder: FormBuilder,
  question: Matrix,
  answers?: QuestionAnswerResponseData[],
  enableValidators?: boolean,
) {
  question.rows.forEach((row) => {
    const rowAnswer = answers?.find((ans) => ans.questionId === row.id);

    switch (row.type) {
      case 'OPENANSWER': {
        const defaultValue =
          rowAnswer && rowAnswer.$type === 'openAnswer'
            ? rowAnswer.answer
            : row.defaultAnswer.enabled
              ? row.defaultAnswer.defaultAnswer
              : '';
        group[row.id] = formBuilder.control(
          defaultValue,
          getValidators(row, enableValidators),
        );
        break;
      }

      case 'SINGLE_CHOICE': {
        handleSingleChoiceQuestion(
          group,
          formBuilder,
          row,
          rowAnswer as SingleSelectResponseData,
          enableValidators,
        );
        break;
      }

      case 'MULTIPLE_CHOICE': {
        handleMultipleChoiceQuestion(
          group,
          formBuilder,
          row as MultiSelect,
          answers,
          enableValidators,
        );
        break;
      }
    }
  });
}

function handleSingleChoiceQuestion(
  group: { [key: string]: AbstractControl<any, any> },
  formBuilder: FormBuilder,
  question: SingleSelect,
  existingAnswer?: SingleSelectResponseData,
  enableValidators?: boolean,
) {
  if (question.presentation.endsWith('SLIDER')) {
    group[question.id] = formBuilder.control(
      getSingleChoiceSliderValue(question, existingAnswer),
      getValidators(question, enableValidators),
    );
  } else {
    const selectedAlternative = getSingleChoiceSelectedAlternative(
      question,
      existingAnswer,
    );
    group[question.id] = formBuilder.control(
      selectedAlternative
        ? selectedAlternative.id
        : question.defaultAnswer || '',
      getValidators(question, enableValidators),
    );
    if (question.alternatives.find((i) => i.type === 'OTHER')) {
      const otherValidators = [];
      if (enableValidators) {
        if (question.mandatory) {
          otherValidators.push(Validators.required);
        }
        otherValidators.push(maxLengthValidator(255, 'utf16'));
      }

      group[`${question.id}-OTHER`] = formBuilder.control(
        existingAnswer?.otherText || '',
        otherValidators,
      );

      if (selectedAlternative?.type !== 'OTHER') {
        group[`${question.id}-OTHER`]?.disable();
      }
    }
  }
}

function getSingleChoiceSliderValue(
  question: SingleSelect,
  existingAnswer?: SingleSelectResponseData,
) {
  if (existingAnswer && existingAnswer.$type === 'singleSelectAnswer') {
    if (existingAnswer.alternative?.length > 0) {
      return question.alternatives.find(
        (alt) => alt.id === existingAnswer.alternative[0],
      )?.orderNumber;
    } else {
      return undefined;
    }
  }
  if (question.slider?.defaultValue) {
    return question.alternatives.find(
      (alt) => alt.id === question.slider?.defaultValue,
    )?.orderNumber;
  }
  return undefined;
}

function getSingleChoiceSelectedAlternative(
  question: SingleSelect,
  existingAnswer?: QuestionAnswerResponseData,
) {
  return existingAnswer && existingAnswer.$type === 'singleSelectAnswer'
    ? question.alternatives.find(
        (alt) => alt.id === existingAnswer.alternative[0],
      )
    : undefined;
}

function handleMultipleChoiceQuestion(
  group: { [key: string]: AbstractControl<any, any> },
  formBuilder: FormBuilder,
  question: MultiSelect,
  answers?: QuestionAnswerResponseData[],
  enableValidators?: boolean,
) {
  const multiSelectAnswer = answers?.find(
    (ans) =>
      ans.questionId === question.id && ans.$type === 'multiSelectAnswer',
  );

  const controlsConfig = createMultipleChoiceControlsConfig(
    formBuilder,
    question,
    multiSelectAnswer,
  );
  const checkboxGroupOptions: AbstractControlOptions = question.mandatory
    ? { validators: getValidators(question, enableValidators) }
    : {};

  group[question.id] = formBuilder.group(controlsConfig, checkboxGroupOptions);

  handleMultipleChoiceOtherOption(
    group,
    formBuilder,
    question,
    multiSelectAnswer,
    enableValidators,
  );

  if (question.mutuallyExclusiveNotApplicable) {
    handleMutuallyExclusiveIdkOption(group, question);
  }
}

function handleMutuallyExclusiveIdkOption(
  group: { [key: string]: AbstractControl<any, any> },
  question: MultiSelect,
) {
  const idkAlternative = question.alternatives.find((alt) => alt.type === 'NA');

  if (!idkAlternative) return;

  const formGroup = group[question.id] as FormGroup;
  const idkControl = formGroup.get(idkAlternative.id);

  if (!idkControl) return;

  const hasOtherSelected = question.alternatives.some(
    (alt) =>
      alt.id !== idkAlternative.id && formGroup.get(alt.id)?.value === true,
  );

  if (hasOtherSelected) {
    idkControl?.disable({ emitEvent: false });
  }

  if (idkControl?.value === true) {
    question.alternatives.forEach((alt) => {
      if (alt.id !== idkAlternative.id) {
        const control = formGroup.get(alt.id);
        control?.disable({ emitEvent: false });
      }
    });
  }

  idkControl?.valueChanges.subscribe((selected) => {
    question.alternatives.forEach((alt) => {
      if (alt.id !== idkAlternative.id) {
        const control = formGroup.get(alt.id);
        if (selected) {
          control?.disable({ emitEvent: false });
        } else {
          control?.enable({ emitEvent: false });
        }
      }
    });
  });

  question.alternatives.forEach((alt) => {
    if (alt.id !== idkAlternative.id) {
      const control = formGroup.get(alt.id);
      control?.valueChanges.subscribe((selected) => {
        const existingAnswer = question.alternatives.some(
          (a) =>
            a.id !== idkAlternative.id && formGroup.get(a.id)?.value === true,
        );

        if (existingAnswer) {
          idkControl?.disable({ emitEvent: false });
        } else {
          idkControl?.enable({ emitEvent: false });
        }
      });
    }
  });
}

function createMultipleChoiceControlsConfig(
  formBuilder: FormBuilder,
  question: MultiSelect,
  multiSelectAnswer?: QuestionAnswerResponseData,
) {
  return question.alternatives.reduce(
    (acc, alt) => {
      const selected =
        multiSelectAnswer && multiSelectAnswer.$type === 'multiSelectAnswer'
          ? multiSelectAnswer.alternatives.includes(alt.id)
          : false;

      acc[alt.id] = formBuilder.control(selected, { nonNullable: true });
      return acc;
    },
    {} as { [key: string]: FormControl<boolean> },
  );
}

function handleMultipleChoiceOtherOption(
  group: { [key: string]: AbstractControl<any, any> },
  formBuilder: FormBuilder,
  question: MultiSelect,
  multiSelectAnswer?: QuestionAnswerResponseData,
  enableValidators?: boolean,
) {
  const otherAlternative = question.alternatives.find(
    (alt) => alt.type === 'OTHER',
  );

  if (otherAlternative) {
    const otherValidators = [];
    if (enableValidators) {
      if (question.mandatory) {
        otherValidators.push(Validators.required);
      }
      otherValidators.push(maxLengthValidator(255, 'utf16'));
    }

    const existingOtherText =
      multiSelectAnswer?.$type === 'multiSelectAnswer'
        ? multiSelectAnswer.otherText || ''
        : '';

    group[`${question.id}-OTHER`] = formBuilder.control(
      existingOtherText,
      otherValidators,
    );

    const isOtherSelected =
      multiSelectAnswer?.$type === 'multiSelectAnswer' &&
      multiSelectAnswer.alternatives.includes(otherAlternative.id);

    if (!isOtherSelected) {
      group[`${question.id}-OTHER`]?.disable();
    }
  }
}

const handleHierarchicalQuestion = (
  group: { [key: string]: AbstractControl<any, any> },
  formBuilder: FormBuilder,
  question: Hierarchical,
  answers?: QuestionAnswerResponseData[],
  enableValidators?: boolean,
) => {
  const hierarchicalGroup = formBuilder.group({});
  question.levels.forEach((level) => {
    const controlName = `${question.id}_${level.id}`;
    const existingAnswer = answers?.find(
      (i) => i.questionId === level.id,
    ) as HierarchicalResponseData;

    const alternative = question.alternatives.find(
      (i) => i.id === existingAnswer?.answer,
    );

    hierarchicalGroup.addControl(
      controlName,
      formBuilder.control(
        alternative,
        getValidators(question, enableValidators),
      ),
    );
  });
  group[question.id] = hierarchicalGroup;
};

const handlePriorityDropdownQuestion = (
  group: { [key: string]: AbstractControl<any, any> },
  formBuilder: FormBuilder,
  question: PriorityDropdown,
  answers?: QuestionAnswerResponseData[],
  enableValidators?: boolean,
) => {
  const priorityDropdownGroup = formBuilder.group({});
  question.rows.forEach((row) => {
    const existingAnswer = answers?.find(
      (i) => i.questionId === row.id,
    ) as PriorityDropdownResponseData;
    const alternative = row.alternatives.find(
      (i) => i.id === existingAnswer?.answer,
    );

    priorityDropdownGroup.addControl(
      row.id,
      formBuilder.control(
        alternative?.id,
        getValidators(question, enableValidators),
      ),
    );
  });

  group[question.id] = priorityDropdownGroup;
};

function handleCalendarQuestion(
  group: { [key: string]: AbstractControl<any, any> },
  formBuilder: FormBuilder,
  question: Calendar,
  answers?: QuestionAnswerResponseData[],
  enableValidators?: boolean,
) {
  const answer = answers?.find(
    (i) => i.questionId == question.id,
  ) as CalendarResponseData;
  group[question.id] = formBuilder.control(
    answer ? new Date(answer.answer) : '',
    getValidators(question, enableValidators),
  );
}

function handleFileUploadQuestion(
  group: { [key: string]: AbstractControl<any, any> },
  formBuilder: FormBuilder,
  question: FileUpload,
  answers?: QuestionAnswerResponseData[],
  enableValidators?: boolean,
) {
  const answer = answers?.find(
    (i) => i.questionId == question.id,
  ) as FileUploadResponseData;
  group[question.id] = formBuilder.control(
    answer?.fileName,
    getValidators(question, enableValidators),
  );
}

const handlePriorityDragDropQuestion = (
  group: { [key: string]: AbstractControl<any, any> },
  formBuilder: FormBuilder,
  question: PriorityDragDrop,
  answers?: DragDropResponseData,
  enableValidators?: boolean,
) => {
  const existingAnswers: PriorityDragDropItem[] =
    answers?.answers
      .map((answerId) => question.items.find((item) => item.id === answerId))
      .filter((item): item is PriorityDragDropItem => !!item) || [];
  group[question.id] = formBuilder.control<PriorityDragDropItem[]>(
    existingAnswers,
    {
      nonNullable: true,
      validators: getValidators(question, enableValidators),
    },
  );
};
